import { Button as AntButton } from "antd";
import { ApiKeyOrGetter } from "../../../services/GeneralService";
import type { ButtonProps } from "antd";
import { Button } from "../../ui/button";
import { SearchIcon } from "lucide-react";
import React, { useState } from "react";

import RequestView from "../RequestView";
import { ChatCompletionRequest } from "../../../model_gen/chat";
import { Link } from "react-router-dom";

export const PrimaryButton: React.FC<
  Omit<ButtonProps, "type"> & {
    isLeadingIcon?: boolean;
    children?: React.ReactNode | string;
  }
> = ({ isLeadingIcon = true, title, children, ...props }) => {
  return (
    <AntButton
      {...props}
      iconPosition={isLeadingIcon ? "start" : "end"}
      type="primary">
      {children ? children : title}
    </AntButton>
  );
};
export const DashedButton: React.FC<
  Omit<ButtonProps, "type"> & {
    isLeadingIcon?: boolean;
    children?: React.ReactNode | string;
  }
> = ({ isLeadingIcon = true, title, children, ...props }) => {
  return (
    <AntButton
      {...props}
      iconPosition={isLeadingIcon ? "start" : "end"}
      type="dashed">
      {children ? children : title}
    </AntButton>
  );
};
export const OutlineButton: React.FC<
  Omit<ButtonProps, "type"> & {
    isLeadingIcon?: boolean;
    children?: React.ReactNode | string;
  }
> = ({ isLeadingIcon = true, title, children, ...props }) => {
  return (
    <AntButton
      {...props}
      iconPosition={isLeadingIcon ? "start" : "end"}
      type="default">
      {children ? children : title}
    </AntButton>
  );
};
export const GhostButton: React.FC<
  Omit<ButtonProps, "type"> & {
    isLeadingIcon?: boolean;
    children?: React.ReactNode | string;
  }
> = ({ isLeadingIcon = true, title, children, ...props }) => {
  return (
    <AntButton
      {...props}
      iconPosition={isLeadingIcon ? "start" : "end"}
      type="text">
      {children ? children : title}
    </AntButton>
  );
};
export const LinkButton: React.FC<
  Omit<ButtonProps, "type"> & {
    isLeadingIcon?: boolean;
    children?: React.ReactNode | string;
  }
> = ({ isLeadingIcon = true, title, children, ...props }) => {
  return (
    <AntButton
      {...props}
      iconPosition={isLeadingIcon ? "start" : "end"}
      type="link">
      {children ? children : title}
    </AntButton>
  );
};

// New BadgeButton component
export const BadgeButton: React.FC<
  ButtonProps & {
    badgeCount?: number;
    badgeColor?: string;
    badgePosition?: {
      top?: string;
      right?: string;
      bottom?: string;
      left?: string;
    };
  }
> = ({
  badgeCount = 0,
  badgeColor = "#ff4d4f",
  badgePosition = { top: "7px", right: "-3px" },
  children,
  ...props
}) => {
  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <AntButton {...props}>{children}</AntButton>
      {badgeCount > 0 && (
        <span
          style={{
            position: "absolute",
            top: badgePosition.top,
            right: badgePosition.right,
            bottom: badgePosition.bottom,
            left: badgePosition.left,
            backgroundColor: badgeColor,
            color: "#fff",
            borderRadius: "50%",
            fontSize: "10px",
            minWidth: "16px",
            height: "16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 0 0 1px #fff",
            zIndex: 1,
          }}>
          {badgeCount > 99 ? "99+" : badgeCount}
        </span>
      )}
    </div>
  );
};

export const RequestViewButton: React.FC<{
  children?: React.ReactNode | string;
  apiKey: ApiKeyOrGetter;
  request?: ChatCompletionRequest;
  requestId?: string;
  applicationId?: string;
  applicationActionId?: string;
  isEditable?: boolean;
  onUpdated?: (updatedRequest: ChatCompletionRequest) => void;
  to?: string;
  showIcon?: boolean;
  systemPrompt?: string;
  testSetId?: string;
  testSetRequestId?: string;
  onClick?: (e: React.MouseEvent) => void;
  buttonStyle?: React.CSSProperties;
}> = ({
  apiKey,
  request,
  requestId,
  applicationId,
  applicationActionId,
  onClick,
  isEditable = false,
  onUpdated,
  children,
  to,
  showIcon = true,
  systemPrompt,
  testSetId,
  testSetRequestId,
  buttonStyle,
}) => {
  const [showOverlay, setShowOverlay] = useState(false);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event bubbling
    onClick?.(e);
  };

  const button = (
    <Button
      variant="ghost"
      size="sm"
      onClick={handleClick}
      className="gap-1"
      style={buttonStyle}
    >
      {showIcon && <SearchIcon className="w-3 h-3" />}
      {children || "View"}
    </Button>
  );

    const buttonWithOverlay = (
    <div
      className="relative group"
      onMouseEnter={() => setShowOverlay(true)}
      onMouseLeave={() => setShowOverlay(false)}
    >
      {button}
      {showOverlay && (
        <div 
          className="absolute z-50 right-0 top-full bg-transparent border-none shadow-none max-w-[600px] w-[600px] pt-1"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          onPointerDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          <RequestView
            request={request}
            apiKey={apiKey}
            isEditable={isEditable}
            requestId={requestId}
            applicationId={applicationId}
            applicationActionId={applicationActionId}
            onUpdated={onUpdated}
            systemPrompt={systemPrompt}
            testSetId={testSetId}
            testSetRequestId={testSetRequestId}
          />
        </div>
      )}
    </div>
  );

  return to ? (
    <Link to={to} target="_blank" style={{ display: "inline-flex" }}>
      {buttonWithOverlay}
    </Link>
  ) : (
    buttonWithOverlay
  );
};
